import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { supabase, getAdminStatus, acceptPendingStoreOwnerInvites } from './supabaseClient';
import './App.css';
import AuthPage from './Auth';
import PublicOrderingPage from './components/PublicOrderingPage';
import PublicStorePage from './components/PublicStorePage';
import PublicSite from './components/PublicSite';
import SerVSoloLandingPage from './components/SerVSoloLandingPage';
import StoreSite from './components/StoreSite';
import PlatformAdminSite from './components/PlatformAdminSite';
import StoreOwnerPortal from './components/StoreOwnerPortal';

const FALLBACK_ADMIN_EMAILS = (process.env.REACT_APP_ADMIN_EMAILS || 'firatu89@gmail.com')
  .split(',')
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

const getStoreSlugFromHostname = () => {
  if (typeof window === 'undefined') return '';
  const hostname = String(window.location.hostname || '').toLowerCase();
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') return '';
  if (hostname === 'servsolo.com' || hostname === 'www.servsolo.com') return '';
  if (hostname.endsWith('.servsolo.com')) {
    return hostname.slice(0, -'.servsolo.com'.length).split('.').filter(Boolean)[0] || '';
  }
  return '';
};

// Renders the store's custom website on its subdomain (slug.servsolo.com).
// Each path-specific page component reads the URL's page key from its own props.
const StoreDomainHomePage = ({ isSignedIn = false }) => {
  const storeSlug = getStoreSlugFromHostname();
  if (!storeSlug) return null;
  return <StoreSite slug={storeSlug} page="home" isSignedIn={isSignedIn} />;
};
const StoreDomainPage = ({ pageKey, isSignedIn = false, fallback = null }) => {
  const storeSlug = getStoreSlugFromHostname();
  if (!storeSlug) return fallback;
  return <StoreSite slug={storeSlug} page={pageKey} isSignedIn={isSignedIn} />;
};
const StoreDomainOrderRoute = ({ mode = 'online' }) => {
  const storeSlug = getStoreSlugFromHostname();
  if (!storeSlug) return <Navigate to="/" replace />;
  return <PublicOrderingPage mode={mode} forcedSlug={storeSlug} />;
};
const StoreDomainRoute = ({ fallback }) => {
  const storeSlug = getStoreSlugFromHostname();
  if (!storeSlug) return fallback;
  return <StoreDomainHomePage />;
};

// On servsolo.com, /features /pricing /contact all render the landing page scrolled to that section.
// On a store subdomain, the store's own page takes over instead.
const MainSitePage = ({ pageKey, isSignedIn, storePageFallback }) => {
  const storeSlug = getStoreSlugFromHostname();
  if (storeSlug) return storePageFallback;
  return <SerVSoloLandingPage isSignedIn={isSignedIn} scrollToSection={pageKey} />;
};

// Listens for postMessage navigation requests from iframes — no full page reload
const NavigationHandler = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'ss_navigate' && e.data?.href) navigate(e.data.href);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [navigate]);
  return null;
};

const LegacyRouteRedirect = ({ section }) => {
  const { restaurantId } = useParams();
  return <Navigate to={`/dashboard/stores/${restaurantId}/${section}`} replace />;
};

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // role: null (resolving) | 'admin' | 'store_owner' | 'no_access'
  const [role, setRole] = useState(null);

  useEffect(() => {
    const fallback = setTimeout(() => setLoading(false), 4000);

    const handleSessionChange = async (s) => {
      clearTimeout(fallback);
      setSession(s);
      if (s) {
        try {
          await acceptPendingStoreOwnerInvites();
        } catch {
          // Role resolution below will still proceed for existing owners/admins.
        }

        setLoading(false);
        resolveRole(s.user, s.access_token);
      } else {
        setLoading(false);
        setRole(null);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      handleSessionChange(s);
    });

    return () => { clearTimeout(fallback); subscription?.unsubscribe(); };
  }, []);

  const resolveRole = async (user, accessToken) => {
    // 1. SerSolo super-admin from server-side trusted admin list.
    try {
      const adminStatus = await getAdminStatus(accessToken);
      if (adminStatus?.isAdmin) {
        setRole('admin');
        return;
      }
    } catch {
      // Fall through to frontend fallback and ownership-based role resolution.
    }

    if (FALLBACK_ADMIN_EMAILS.includes((user?.email || '').toLowerCase())) {
      setRole('admin');
      return;
    }

    // 2. Store Owner access is always assignment-based.
    try {
      const { data } = await supabase
        .from('restaurants')
        .select('slug, id')
        .eq('store_owner_id', user.id)
        .eq('lifecycle_status', 'active')
        .limit(1)
        .maybeSingle();
      if (data?.id) {
        setRole('store_owner');
        return;
      }
    } catch { /* fall through */ }

    // 3. Fallback
    setRole('no_access');
  };

  const getSignedInHome = () => {
    if (role === 'no_access') return '/portal';
    return '/dashboard';
  };

  if (loading || (session && !role)) return <p>Loading...</p>;

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <div className="App">
        <NavigationHandler />
        <Routes>
          <Route path="/store/:slug/:pageKey" element={<PublicStorePage session={session} />} />
          <Route path="/store/:slug" element={<PublicStorePage session={session} />} />
          <Route path="/kiosk/:slug" element={<PublicOrderingPage mode="kiosk" />} />
          <Route path="/order/:slug" element={<PublicOrderingPage mode="online" />} />
          <Route path="/kiosk" element={<StoreDomainOrderRoute mode="kiosk" />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/about" element={<StoreDomainPage pageKey="about" isSignedIn={!!session} />} />
          <Route path="/features" element={<MainSitePage pageKey="features" isSignedIn={!!session} storePageFallback={<StoreDomainPage pageKey="features" isSignedIn={!!session} />} />} />
          <Route path="/pricing" element={<MainSitePage pageKey="pricing" isSignedIn={!!session} storePageFallback={<StoreDomainPage pageKey="pricing" isSignedIn={!!session} />} />} />
          <Route path="/demo" element={<MainSitePage pageKey="pricing" isSignedIn={!!session} storePageFallback={<StoreDomainPage pageKey="demo" isSignedIn={!!session} />} />} />
          <Route path="/contact" element={<MainSitePage pageKey="contact" isSignedIn={!!session} storePageFallback={<StoreDomainPage pageKey="contact" isSignedIn={!!session} />} />} />
          <Route path="/test" element={<StoreDomainPage pageKey="test" isSignedIn={!!session} fallback={<PublicSite page="test" isSignedIn={!!session} />} />} />
          <Route path="/:slug/kiosk" element={<PublicOrderingPage mode="kiosk" />} />
          <Route path="/:slug" element={<PublicOrderingPage mode="online" />} />

          {!session ? (
            <>
              <Route path="/" element={<StoreDomainRoute fallback={<SerVSoloLandingPage isSignedIn={false} />} />} />
              <Route path="/about" element={<StoreDomainPage pageKey="about" />} />
              <Route path="/contact" element={<StoreDomainPage pageKey="contact" />} />
              <Route path="/order" element={<StoreDomainOrderRoute mode="online" />} />
              <Route path="/login" element={<AuthPage />} />
              <Route path="/signup" element={<Navigate to="/login" replace />} />
              <Route path="/dashboard" element={<Navigate to="/login" replace />} />
              <Route path="/dashboard/*" element={<Navigate to="/login" replace />} />
              <Route path="/admin" element={<Navigate to="/login" replace />} />
              <Route path="/admin/restaurants" element={<Navigate to="/login" replace />} />
              <Route path="/admin/users" element={<Navigate to="/login" replace />} />
              <Route path="/admin/invitations" element={<Navigate to="/login" replace />} />
              <Route path="/admin/requests" element={<Navigate to="/login" replace />} />
              <Route path="/admin/messages" element={<Navigate to="/login" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>

          ) : role === 'admin' ? (
            <>
              <Route path="/" element={<StoreDomainRoute fallback={<SerVSoloLandingPage isSignedIn />} />} />
              <Route path="/about" element={<StoreDomainPage pageKey="about" isSignedIn />} />
              <Route path="/contact" element={<StoreDomainPage pageKey="contact" isSignedIn />} />
              <Route path="/order" element={<StoreDomainOrderRoute mode="online" />} />
              <Route path="/kiosk" element={<StoreDomainOrderRoute mode="kiosk" />} />
              <Route path="/login" element={<Navigate to="/dashboard" replace />} />
              <Route path="/signup" element={<Navigate to="/dashboard" replace />} />
              <Route path="/portal" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<PlatformAdminSite page="stores" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/overview" element={<PlatformAdminSite page="overview" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/restaurants" element={<PlatformAdminSite page="restaurants" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/users" element={<PlatformAdminSite page="users" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/invitations" element={<PlatformAdminSite page="invitations" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/requests" element={<PlatformAdminSite page="requests" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/messages" element={<PlatformAdminSite page="messages" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/site-content" element={<PlatformAdminSite page="site-content" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/admin" element={<PlatformAdminSite page="store-editor" storeSection="admin" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/display" element={<PlatformAdminSite page="store-editor" storeSection="display" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/products" element={<PlatformAdminSite page="store-editor" storeSection="products" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/modifiers" element={<PlatformAdminSite page="store-editor" storeSection="modifiers" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/store" element={<PlatformAdminSite page="store-editor" storeSection="store" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/style" element={<PlatformAdminSite page="store-editor" storeSection="style" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/staff" element={<PlatformAdminSite page="store-editor" storeSection="staff" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/pages" element={<PlatformAdminSite page="store-editor" storeSection="website" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/website" element={<PlatformAdminSite page="store-editor" storeSection="website" user={session.user} role={role} isAdmin session={session} />} />
              <Route path="/admin" element={<Navigate to="/dashboard/overview" replace />} />
              <Route path="/admin/restaurants" element={<Navigate to="/dashboard/restaurants" replace />} />
              <Route path="/admin/users" element={<Navigate to="/dashboard/users" replace />} />
              <Route path="/admin/invitations" element={<Navigate to="/dashboard/invitations" replace />} />
              <Route path="/admin/requests" element={<Navigate to="/dashboard/requests" replace />} />
              <Route path="/admin/messages" element={<Navigate to="/dashboard/messages" replace />} />
              <Route path="/admin/site-content" element={<Navigate to="/dashboard/site-content" replace />} />
              <Route path="/restaurant/:restaurantId/admin" element={<LegacyRouteRedirect section="admin" />} />
              <Route path="/restaurant/:restaurantId/display" element={<LegacyRouteRedirect section="display" />} />
              <Route path="/restaurant/:restaurantId/products" element={<LegacyRouteRedirect section="products" />} />
              <Route path="/restaurant/:restaurantId/modifiers" element={<LegacyRouteRedirect section="modifiers" />} />
              <Route path="/restaurant/:restaurantId/store" element={<LegacyRouteRedirect section="store" />} />
              <Route path="/restaurant/:restaurantId/style" element={<LegacyRouteRedirect section="style" />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </>

          ) : role === 'store_owner' ? (
            <>
              <Route path="/" element={<StoreDomainRoute fallback={<SerVSoloLandingPage isSignedIn />} />} />
              <Route path="/about" element={<StoreDomainPage pageKey="about" isSignedIn />} />
              <Route path="/contact" element={<StoreDomainPage pageKey="contact" isSignedIn />} />
              <Route path="/order" element={<StoreDomainOrderRoute mode="online" />} />
              <Route path="/kiosk" element={<StoreDomainOrderRoute mode="kiosk" />} />
              <Route path="/login" element={<Navigate to={getSignedInHome()} replace />} />
              <Route path="/signup" element={<Navigate to={getSignedInHome()} replace />} />
              <Route path="/portal" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<PlatformAdminSite page="stores" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/messages" element={<PlatformAdminSite page="messages" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/account" element={<PlatformAdminSite page="account" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/display" element={<PlatformAdminSite page="store-editor" storeSection="display" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/products" element={<PlatformAdminSite page="store-editor" storeSection="products" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/modifiers" element={<PlatformAdminSite page="store-editor" storeSection="modifiers" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/store" element={<PlatformAdminSite page="store-editor" storeSection="store" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/style" element={<PlatformAdminSite page="store-editor" storeSection="style" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/staff" element={<PlatformAdminSite page="store-editor" storeSection="staff" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/admin" element={<PlatformAdminSite page="store-editor" storeSection="admin" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/pages" element={<PlatformAdminSite page="store-editor" storeSection="website" user={session.user} role={role} session={session} />} />
              <Route path="/dashboard/stores/:restaurantId/website" element={<PlatformAdminSite page="store-editor" storeSection="website" user={session.user} role={role} session={session} />} />
              <Route path="/admin" element={<Navigate to={getSignedInHome()} replace />} />
              <Route path="/admin/restaurants" element={<Navigate to={getSignedInHome()} replace />} />
              <Route path="/admin/users" element={<Navigate to={getSignedInHome()} replace />} />
              <Route path="/admin/invitations" element={<Navigate to={getSignedInHome()} replace />} />
              <Route path="/admin/requests" element={<Navigate to={getSignedInHome()} replace />} />
              <Route path="/admin/messages" element={<Navigate to={getSignedInHome()} replace />} />
              <Route path="/restaurant/:restaurantId/display" element={<LegacyRouteRedirect section="display" />} />
              <Route path="/restaurant/:restaurantId/products" element={<LegacyRouteRedirect section="products" />} />
              <Route path="/restaurant/:restaurantId/modifiers" element={<LegacyRouteRedirect section="modifiers" />} />
              <Route path="/restaurant/:restaurantId/store" element={<LegacyRouteRedirect section="store" />} />
              <Route path="/restaurant/:restaurantId/style" element={<LegacyRouteRedirect section="style" />} />
              <Route path="*" element={<Navigate to={getSignedInHome()} replace />} />
            </>

          ) : role === 'no_access' ? (
            <>
              <Route path="/" element={<StoreDomainRoute fallback={<SerVSoloLandingPage isSignedIn />} />} />
              <Route path="/about" element={<StoreDomainPage pageKey="about" isSignedIn />} />
              <Route path="/contact" element={<StoreDomainPage pageKey="contact" isSignedIn />} />
              <Route path="/order" element={<StoreDomainOrderRoute mode="online" />} />
              <Route path="/login" element={<Navigate to="/portal" replace />} />
              <Route path="/signup" element={<Navigate to="/portal" replace />} />
              <Route path="/dashboard" element={<Navigate to="/portal" replace />} />
              <Route path="/access-pending" element={<Navigate to="/portal" replace />} />
              <Route path="/portal" element={<StoreOwnerPortal page="onboarding" onboardingOnly user={session.user} session={session} />} />
              <Route path="/portal/messages" element={<Navigate to="/portal" replace />} />
              <Route path="/portal/account" element={<Navigate to="/portal" replace />} />
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </>

          ) : (
            <>
              <Route path="*" element={<Navigate to="/portal" replace />} />
            </>
          )}
        </Routes>
      </div>
    </Router>
  );
}

export default App;
